<template>
  <div class="step-content">
    <h5 class="mb-3">
      <i class="feather icon-bar-chart mr-2"></i>
      Dados Econômicos e Operacionais
    </h5>
    <p class="text-muted mb-4">
      Informe os dados econômicos e operacionais da entidade para análise do credenciamento.
    </p>

    <b-alert variant="info" show class="mt-4">
      <div class="alert-body">
        <h6 class="alert-heading">
          <i class="feather icon-info mr-1"></i>
          Informações Importantes
        </h6>
        <hr>
        <ul class="mb-0">
          <li><strong>Período de Referência:</strong> Deve corresponder ao período de vigência do certificado</li>
          <li><strong>Bolsas:</strong> Considere apenas bolsas de estudo regulares oferecidas pela entidade</li>
          <li><strong>Tipos de Bolsas:</strong> Integral (100%) e/ou Parcial (percentual variável)</li>
        </ul>
      </div>
    </b-alert>

    <!-- Período de Referência do Certificado -->
    <h6 class="mt-4 mb-3 text-muted">Período de Referência do Certificado</h6>

    <b-row>
      <b-col md="6">
        <b-form-group label="Data de Início" label-class="required" label-for="certificatePeriodStart">
          <b-form-datepicker
            id="certificatePeriodStart"
            :value="formData.certificate_period_start"
            placeholder="Selecione a data de início"
            :state="getFieldState('certificate_period_start')"
            :disabled="disabled"
            locale="pt-BR"
            :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
            @input="updateField('certificate_period_start', $event)"
          />
          <small class="form-text text-muted">
            <i class="feather icon-calendar mr-1"></i>
            Data de início do período do certificado
          </small>
          <b-form-invalid-feedback>
            Data de início do período é obrigatória
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="Data de Fim" label-class="required" label-for="certificatePeriodEnd">
          <b-form-datepicker
            id="certificatePeriodEnd"
            :value="formData.certificate_period_end"
            placeholder="Selecione a data de fim"
            :state="getFieldState('certificate_period_end')"
            :disabled="disabled"
            locale="pt-BR"
            :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
            :min="formData.certificate_period_start"
            @input="updateField('certificate_period_end', $event)"
          />
          <small class="form-text text-muted">
            <i class="feather icon-calendar mr-1"></i>
            A data de fim deve ser posterior à data de início
          </small>
          <b-form-invalid-feedback>
            Data de fim do período é obrigatória
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Informações sobre Bolsas -->
    <h6 class="mt-4 mb-3 text-muted">Informações sobre Bolsas de Estudo</h6>

    <!-- Número de Bolsas -->
    <b-row>
      <b-col md="6">
        <b-form-group label="Número de Bolsas Ofertadas" label-class="required" label-for="scholarshipCount">
          <b-form-input
            id="scholarshipCount"
            :value="formData.scholarship_count"
            type="number"
            min="0"
            placeholder="Digite o número de bolsas"
            :state="getFieldState('scholarship_count')"
            :disabled="disabled"
            class="required"
            @input="updateField('scholarship_count', $event)"
          />
          <small class="form-text text-muted">
            <i class="feather icon-award mr-1"></i>
            Total de bolsas de estudo oferecidas pela entidade
          </small>
          <b-form-invalid-feedback>
            Número de bolsas é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Tipos de Bolsas -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Tipos de Bolsas" label-class="required">
          <b-form-checkbox-group
            v-model="selectedTypes"
            :options="scholarshipTypeOptions"
            :state="getFieldState('scholarship_types')"
            :disabled="disabled"
            :class="!primaryVariant ? 'checkbox-dynamic-primary' : ''"
            stacked
            @change="handleScholarshipTypesChange"
          />
          <small class="form-text text-muted">
            <i class="feather icon-check-square mr-1"></i>
            Selecione todos os tipos de bolsas oferecidas pela entidade
          </small>
          <b-form-invalid-feedback>
            Selecione pelo menos um tipo de bolsa
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Serviços de Apoio aos Bolsistas -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Serviços de Apoio aos Bolsistas" label-for="supportServices">
          <b-form-textarea
            id="supportServices"
            :value="formData.support_services"
            placeholder="Descreva os serviços de apoio oferecidos aos bolsistas (opcional)"
            rows="4"
            :state="getFieldState('support_services')"
            :disabled="disabled"
            @input="updateField('support_services', $event)"
          />
          <small class="form-text text-muted">
            <i class="feather icon-users mr-1"></i>
            Descreva programas de tutoria, apoio psicopedagógico, atividades complementares, etc. (Campo opcional)
          </small>
          <b-form-invalid-feedback>
            Erro na descrição dos serviços de apoio
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Documentação -->
    <h6 class="mt-4 mb-3 text-muted">Documentação</h6>

    <!-- Relatório de Atividades Desempenhadas -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Relatório de Atividades Desempenhadas" label-class="required" label-for="reportActivities">
          <b-form-file
            id="reportActivities"
            :value="formData.report_activities ? formData.report_activities.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('report_activities')"
            :disabled="disabled"
            @input="updateField('report_activities', $event)"
          />
          <template v-if="formData && formData.report_activities">
            <small v-if="formData.report_activities.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.report_activities.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Relatório de atividades é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Relatório Sintético de Concessão de Bolsas -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Relatório Sintético de Concessão de Bolsas" label-class="required" label-for="summaryReportScholarships">
          <b-form-file
            id="summaryReportScholarships"
            :value="formData.summary_report_scholarships ? formData.summary_report_scholarships.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('summary_report_scholarships')"
            :disabled="disabled"
            @input="updateField('summary_report_scholarships', $event)"
          />
          <template v-if="formData && formData.summary_report_scholarships">
            <small v-if="formData.summary_report_scholarships.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.summary_report_scholarships.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Relatório sintético de bolsas é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Relação de Bolsas de Estudo -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Relação de Bolsas de Estudo" label-class="required" label-for="listScholarships">
          <b-form-file
            id="listScholarships"
            :value="formData.list_scholarships ? formData.list_scholarships.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('list_scholarships')"
            :disabled="disabled"
            @input="updateField('list_scholarships', $event)"
          />
          <template v-if="formData && formData.list_scholarships">
            <small v-if="formData.list_scholarships.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.list_scholarships.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Relação de bolsas de estudo é obrigatória
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Análise do Perfil Socioeconômico dos Alunos Bolsistas -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Análise do Perfil Socioeconômico dos Alunos Bolsistas" label-class="required" label-for="analysisSocioeconomicStudents">
          <b-form-file
            id="analysisSocioeconomicStudents"
            :value="formData.analysis_socioeconomic_students ? formData.analysis_socioeconomic_students.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('analysis_socioeconomic_students')"
            :disabled="disabled"
            @input="updateField('analysis_socioeconomic_students', $event)"
          />
          <template v-if="formData && formData.analysis_socioeconomic_students">
            <small v-if="formData.analysis_socioeconomic_students.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.analysis_socioeconomic_students.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Análise do perfil socioeconômico é obrigatória
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Regime Contábil -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Regime Contábil" label-class="required" label-for="accountingRegime">
          <b-form-file
            id="accountingRegime"
            :value="formData.accounting_regime ? formData.accounting_regime.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('accounting_regime')"
            :disabled="disabled"
            @input="updateField('accounting_regime', $event)"
          />
          <template v-if="formData && formData.accounting_regime">
            <small v-if="formData.accounting_regime.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.accounting_regime.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Regime contábil é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Relatórios Contábeis -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Relatórios Contábeis" label-class="required" label-for="accountingReports">
          <b-form-file
            id="accountingReports"
            :value="formData.accounting_reports ? formData.accounting_reports.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('accounting_reports')"
            :disabled="disabled"
            @input="updateField('accounting_reports', $event)"
          />
          <template v-if="formData && formData.accounting_reports">
            <small v-if="formData.accounting_reports.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.accounting_reports.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Relatórios contábeis são obrigatórios
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Demonstração Contábil da Gratuidade -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Demonstração Contábil da Gratuidade" label-class="required" label-for="accountingGratuityDemo">
          <b-form-file
            id="accountingGratuityDemo"
            :value="formData.accounting_gratuity_demo ? formData.accounting_gratuity_demo.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('accounting_gratuity_demo')"
            :disabled="disabled"
            @input="updateField('accounting_gratuity_demo', $event)"
          />
          <template v-if="formData && formData.accounting_gratuity_demo">
            <small v-if="formData.accounting_gratuity_demo.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.accounting_gratuity_demo.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Demonstração contábil da gratuidade é obrigatória
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Parecer de Auditoria Independente -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Parecer de Auditoria Independente do Exercício em Análise" label-class="required" label-for="auditOpinion">
          <b-form-file
            id="auditOpinion"
            :value="formData.audit_opinion ? formData.audit_opinion.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('audit_opinion')"
            :disabled="disabled"
            @input="updateField('audit_opinion', $event)"
          />
          <template v-if="formData && formData.audit_opinion">
            <small v-if="formData.audit_opinion.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.audit_opinion.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Parecer de auditoria independente é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Receita Efetivamente Recebida -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Receita Efetivamente Recebida" label-class="required" label-for="revenueReceived">
          <b-form-file
            id="revenueReceived"
            :value="formData.revenue_received ? formData.revenue_received.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('revenue_received')"
            :disabled="disabled"
            @input="updateField('revenue_received', $event)"
          />
          <template v-if="formData && formData.revenue_received">
            <small v-if="formData.revenue_received.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.revenue_received.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Receita efetivamente recebida é obrigatória
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Plano de Atendimento -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Plano de Atendimento" label-class="required" label-for="servicePlan">
          <b-form-file
            id="servicePlan"
            :value="formData.service_plan ? formData.service_plan.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('service_plan')"
            :disabled="disabled"
            @input="updateField('service_plan', $event)"
          />
          <template v-if="formData && formData.service_plan">
            <small v-if="formData.service_plan.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.service_plan.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB
          </small>
          <b-form-invalid-feedback>
            Plano de atendimento é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Inscrição dos Serviços Socioassistenciais -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Inscrição dos Serviços Socioassistenciais" label-for="socialAssistanceRegistration">
          <b-form-file
            id="socialAssistanceRegistration"
            :value="formData.social_assistance_registration ? formData.social_assistance_registration.file : null"
            placeholder="Clique para selecionar o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('social_assistance_registration')"
            :disabled="disabled"
            @input="updateField('social_assistance_registration', $event)"
          />
          <template v-if="formData && formData.social_assistance_registration">
            <small v-if="formData.social_assistance_registration.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.social_assistance_registration.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <small class="form-text text-muted">
            <i class="feather icon-file-text mr-1"></i>
            Apenas arquivos PDF. Tamanho máximo: 10MB. Campo opcional.
          </small>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Lista de arquivos carregados -->
    <div v-if="hasUploadedFiles" class="uploaded-files mt-4">
      <h6 class="text-muted mb-3">
        <i class="feather icon-check-circle mr-1"></i>
        Arquivos Carregados
      </h6>
      <b-card>
        <b-list-group>
          <b-list-group-item
            v-for="(file, key) in uploadedFiles"
            :key="key"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <i class="feather icon-file-text text-danger mr-2"></i>
              <strong>{{ getDocumentTitle(key) }}</strong>
              <div class="text-muted small">
                <a v-if="getDocumentUrl(uploadedFiles[key])" :href="getDocumentUrl(uploadedFiles[key])" target="_blank" class="mr-1 no-print download-link text-primary" title="Baixar arquivo">
                  <i class="feather icon-download"></i>
                </a>
                {{ getFileName(key) }}
                <small class="ml-2">({{ formatFileSize(getFileSize(key)) }})</small>
                <small class="ml-2 text-muted">[{{ getFileExtension(getFileName(key)) }}]</small>
              </div>
            </div>
            <!--
            <b-button
              variant="outline-danger"
              size="sm"
              @click="removeFile(key)">
              <i class="feather icon-trash-2"></i>
            </b-button>
            -->
          </b-list-group-item>
        </b-list-group>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckboxGroup,
  BFormInvalidFeedback,
  BFormDatepicker,
  BFormFile,
  BAlert,
  BCard,
  BListGroup,
  BListGroupItem,
  BButton
} from 'bootstrap-vue'

export default {
  name: 'EconomicOperationalDataStep',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckboxGroup,
    BFormInvalidFeedback,
    BFormDatepicker,
    BFormFile,
    BAlert,
    BCard,
    BListGroup,
    BListGroupItem,
    BButton
  },
  props: {
    formData: {
      type: Object,
      required: true,
      default: () => ({
        certificate_period_start: null,
        certificate_period_end: null,
        scholarship_count: '',
        scholarship_types: '',
        support_services: '',
        report_activities: null,
        summary_report_scholarships: null,
        list_scholarships: null,
        analysis_socioeconomic_students: null,
        accounting_regime: null,
        accounting_reports: null,
        accounting_gratuity_demo: null,
        audit_opinion: null,
        revenue_received: null,
        service_plan: null,
        social_assistance_registration: null
      })
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    primaryVariant: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:form-data', 'upload-document', 'remove-file'],
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development' ||
             window.location.hostname === 'localhost' ||
             window.location.hostname === '127.0.0.1' ||
             window.location.hostname.includes('localhost')
    },
    hasUploadedFiles() {
      return (this.formData.report_activities && this.formData.report_activities !== null) ||
             (this.formData.summary_report_scholarships && this.formData.summary_report_scholarships !== null) ||
             (this.formData.list_scholarships && this.formData.list_scholarships !== null) ||
             (this.formData.analysis_socioeconomic_students && this.formData.analysis_socioeconomic_students !== null) ||
             (this.formData.accounting_regime && this.formData.accounting_regime !== null) ||
             (this.formData.accounting_reports && this.formData.accounting_reports !== null) ||
             (this.formData.accounting_gratuity_demo && this.formData.accounting_gratuity_demo !== null) ||
             (this.formData.audit_opinion && this.formData.audit_opinion !== null) ||
             (this.formData.revenue_received && this.formData.revenue_received !== null) ||
             (this.formData.service_plan && this.formData.service_plan !== null) ||
             (this.formData.social_assistance_registration && this.formData.social_assistance_registration !== null);
    },
    uploadedFiles() {
      const economicFiles = {};
      if (this.formData.report_activities && this.formData.report_activities !== null) {
        economicFiles.report_activities = this.formData.report_activities;
      }
      if (this.formData.summary_report_scholarships && this.formData.summary_report_scholarships !== null) {
        economicFiles.summary_report_scholarships = this.formData.summary_report_scholarships;
      }
      if (this.formData.list_scholarships && this.formData.list_scholarships !== null) {
        economicFiles.list_scholarships = this.formData.list_scholarships;
      }
      if (this.formData.analysis_socioeconomic_students && this.formData.analysis_socioeconomic_students !== null) {
        economicFiles.analysis_socioeconomic_students = this.formData.analysis_socioeconomic_students;
      }
      if (this.formData.accounting_regime && this.formData.accounting_regime !== null) {
        economicFiles.accounting_regime = this.formData.accounting_regime;
      }
      if (this.formData.accounting_reports && this.formData.accounting_reports !== null) {
        economicFiles.accounting_reports = this.formData.accounting_reports;
      }
      if (this.formData.accounting_gratuity_demo && this.formData.accounting_gratuity_demo !== null) {
        economicFiles.accounting_gratuity_demo = this.formData.accounting_gratuity_demo;
      }
      if (this.formData.audit_opinion && this.formData.audit_opinion !== null) {
        economicFiles.audit_opinion = this.formData.audit_opinion;
      }
      if (this.formData.revenue_received && this.formData.revenue_received !== null) {
        economicFiles.revenue_received = this.formData.revenue_received;
      }
      if (this.formData.service_plan && this.formData.service_plan !== null) {
        economicFiles.service_plan = this.formData.service_plan;
      }
      if (this.formData.social_assistance_registration && this.formData.social_assistance_registration !== null) {
        economicFiles.social_assistance_registration = this.formData.social_assistance_registration;
      }
      return economicFiles;
    }
  },
  data() {
    return {
      scholarshipTypeOptions: [
        { value: 'IT', text: 'Integral (100%)' },
        { value: 'PT', text: 'Parcial' }
      ],
      selectedTypes: []
    }
  },
  created() {
    console.log('Component created with initial value:', this.formData.scholarship_types);
    this.updateSelectedTypes(this.formData.scholarship_types);
  },
  watch: {
    'formData.scholarship_types': {
      immediate: true,
      handler(newValue) {
        console.log('Watcher triggered with value:', newValue);
        this.updateSelectedTypes(newValue);
      }
    }
  },
  methods: {
    updateSelectedTypes(value) {
      console.log('Updating selected types with value:', value);
      if (value === 'BO') {
        this.selectedTypes = ['IT', 'PT'];
      } else if (value === 'IT') {
        this.selectedTypes = ['IT'];
      } else if (value === 'PT') {
        this.selectedTypes = ['PT'];
      } else {
        this.selectedTypes = [];
      }
      console.log('Selected types updated to:', this.selectedTypes);
    },
    handleScholarshipTypesChange(value) {
      console.log('Checkbox change:', value);
      let apiValue = '';
      if (value.length === 2) {
        apiValue = 'BO';
      } else if (value.length === 1) {
        apiValue = value[0];
      }
      console.log('Sending to API:', apiValue);
      this.$emit('update:form-data', {
        ...this.formData,
        scholarship_types: apiValue
      });
    },
    updateField(fieldName, value) {
      if (fieldName !== 'scholarship_types') {
        // Se é um arquivo (report_activities, summary_report_scholarships, list_scholarships ou analysis_socioeconomic_students)
        if ((fieldName === 'report_activities' || fieldName === 'summary_report_scholarships' || fieldName === 'list_scholarships' || fieldName === 'analysis_socioeconomic_students' || fieldName === 'accounting_regime' || fieldName === 'accounting_reports' || fieldName === 'accounting_gratuity_demo' || fieldName === 'audit_opinion' || fieldName === 'revenue_received' || fieldName === 'service_plan' || fieldName === 'social_assistance_registration') && value) {
          // Emite o evento de upload para o componente pai
          this.$emit('upload-document', {
            fieldName,
            file: value,
            metadata: {
              original_name: value.name,
              size: value.size,
              type: value.type,
              last_modified: value.lastModified,
              document_type: fieldName
            }
          });

          // Atualiza o formData local com status de upload iniciado
          this.$emit('update:form-data', {
            ...this.formData,
            [fieldName]: {
              file: value,
              uploaded: false,
              metadata: {
                original_name: value.name,
                size: value.size,
                type: value.type,
                last_modified: value.lastModified
              }
            }
          });
        } else {
          // Para outros campos que não são arquivos
          this.$emit('update:form-data', {
            ...this.formData,
            [fieldName]: value
          });
        }
      }
    },
    getFieldState(fieldName) {
      if (this.errors[fieldName]) {
        return false
      }
      return null
    },
    formatFileSize(size) {
      if (!size) return '';
      if (size < 1024) return size + ' B';
      if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
      return (size / (1024 * 1024)).toFixed(1) + ' MB';
    },
    removeFile(fieldName) {
      this.$emit('remove-file', fieldName);
    },
    getFileExtension(filename) {
      if (!filename) return '';
      const parts = filename.split('.');
      return parts.length > 1 ? parts.pop().toUpperCase() : '';
    },
    getDocumentTitle(key) {
      const titles = {
        report_activities: 'Relatório de Atividades Desempenhadas',
        summary_report_scholarships: 'Relatório Sintético de Concessão de Bolsas',
        list_scholarships: 'Relação de Bolsas de Estudo',
        analysis_socioeconomic_students: 'Análise do Perfil Socioeconômico dos Alunos Bolsistas',
        accounting_regime: 'Regime Contábil',
        accounting_reports: 'Relatórios Contábeis',
        accounting_gratuity_demo: 'Demonstração Contábil da Gratuidade',
        audit_opinion: 'Parecer de Auditoria Independente do Exercício em Análise',
        revenue_received: 'Receita Efetivamente Recebida',
        service_plan: 'Plano de Atendimento',
        social_assistance_registration: 'Inscrição dos Serviços Socioassistenciais'
      };
      return titles[key] || key;
    },
    getFileSize(key) {
      const file = this.formData[key];
      if (!file) return 0;

      // Se o arquivo já foi carregado com sucesso
      if (file.size) {
        return file.size;
      }

      // Se o arquivo está sendo carregado
      if (file.file) {
        return file.file.size;
      }

      return 0;
    },
    getFileName(key) {
      const file = this.formData[key];
      if (!file) return '';

      // Se o arquivo já foi carregado com sucesso
      if (file.file_name) {
        return file.file_name;
      }

      // Se o arquivo está sendo carregado
      if (file.file) {
        return file.file.name;
      }

      // Se tem metadata com nome original
      if (file.metadata && file.metadata.original_name) {
        return file.metadata.original_name;
      }

      return '';
    },
    getDocumentUrl(document) {
      if (!document) {
        return null;
      }

      // Tentar diferentes propriedades de URL - url_tmp primeiro
      if (document.url_tmp) {
        return document.url_tmp;
      }
      if (document.url_temp) {
        return document.url_temp;
      }
      if (document.url) {
        return document.url;
      }
      if (document.download_url) {
        return document.download_url;
      }

      // Verificar se há metadata com URLs
      if (document.metadata && document.metadata.url_tmp) {
        return document.metadata.url_tmp;
      }
      if (document.metadata && document.metadata.url_temp) {
        return document.metadata.url_temp;
      }
      if (document.metadata && document.metadata.url) {
        return document.metadata.url;
      }

      return null;
    }
  }
}
</script>

<style lang="scss" scoped>
.step-content {
  padding: 1rem 0;

  h5 {
    color: #333;
    font-weight: 600;

    .feather {
      color: var(--dynamic-primary, #156440);
    }
  }

  h6 {
    font-weight: 600;
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 0.5rem;
  }

  .alert {
    border-left: 4px solid var(--bs-info, #00cfe8);
  }
}

/* Inputs com foco usando cores dinâmicas */
.step-content .form-control:focus {
  border-color: var(--dynamic-primary, #156440) !important;
  box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
}

.step-content .form-control.is-valid {
  border-color: var(--bs-success, #28c76f) !important;
}

.step-content .form-control.is-invalid {
  border-color: var(--bs-danger, #ea5455) !important;
}

/* Estilos para labels com classe required */
.required {
  /* Classe mantida para compatibilidade com validações */
}

/* Melhorar a compatibilidade com o CSS já existente do projeto para campos required */
.step-content .required.form-control,
.step-content .form-control.required {
  border-left: 3px solid #ff9f43 !important;
  transition: border-color 0.5s ease-out;
}

.step-content .required.form-control:focus,
.step-content .form-control.required:focus {
  border-left-color: var(--dynamic-primary, #156440) !important;
  border-color: var(--dynamic-primary, #156440) !important;
  box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
}

/* Estilos para datepicker */
.step-content .form-control[readonly] {
  background-color: #fff;
  cursor: pointer;
}

/* Checkboxes com cores dinâmicas */
.checkbox-dynamic-primary {
  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: var(--dynamic-primary, #156440) !important;
    border-color: var(--dynamic-primary, #156440) !important;
  }

  ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
    box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
    border-color: var(--dynamic-primary, #156440) !important;
  }

  ::v-deep .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
    border-color: var(--dynamic-primary, #156440) !important;
  }

  ::v-deep .custom-control-input:not(:disabled):active ~ .custom-control-label::before {
    background-color: var(--dynamic-primary-light, var(--dynamic-primary, #156440)) !important;
    border-color: var(--dynamic-primary-light, var(--dynamic-primary, #156440)) !important;
  }

  /* Suporte para estruturas alternativas do Bootstrap Vue */
  ::v-deep .form-check-input:checked {
    background-color: var(--dynamic-primary, #156440) !important;
    border-color: var(--dynamic-primary, #156440) !important;
  }

  ::v-deep .form-check-input:focus {
    border-color: var(--dynamic-primary, #156440) !important;
    box-shadow: 0 0 0 0.25rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
  }

  /* Para Bootstrap 4 */
  ::v-deep input[type="checkbox"]:checked {
    background-color: var(--dynamic-primary, #156440) !important;
    border-color: var(--dynamic-primary, #156440) !important;
  }

  ::v-deep input[type="checkbox"]:focus {
    border-color: var(--dynamic-primary, #156440) !important;
    box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
  }
}

/* Estilos para componentes de upload */
.form-file {
  margin-bottom: 0.5rem;
}

.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-text .feather {
  font-size: 0.8rem;
}

/* Estilo para lista de arquivos carregados */
.uploaded-files .card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}

.uploaded-files ul li {
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
}

.uploaded-files .feather {
  flex-shrink: 0;
}

/* Estilos para drag and drop */
.custom-file-input:focus ~ .custom-file-label {
  border-color: var(--bs-primary, #007bff);
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Indicadores visuais para diferentes tipos de arquivo */
.text-success {
  color: #28a745 !important;
}
</style>
